<template>
  <v-card elevation="12" class="mt-4">
    <v-card-title>
      Notification Activity
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="activeTab" mandatory dense color="primary">
        <v-btn value="emails" small>
          Emails ({{ emailCount }})
        </v-btn>
        <v-btn value="notifications" small>
          Notifications ({{ notificationCount }})
        </v-btn>
      </v-btn-toggle>
    </v-card-title>
    <v-card-text>
      <template>
        <div v-if="!loading && notificationItems.length === 0 && !hasAccess" class="text-center pa-8">
          <v-icon size="64" color="warning" class="mb-4">mdi-shield-alert-outline</v-icon>
          <h2 class="text-h5 font-weight-bold mb-2">Cannot View Activity</h2>
          <p class="text-body-1 grey--text">
            You can only view activity for users in your current facility.
          </p>
        </div>
        <div v-else-if="!loading && notificationItems.length === 0 && hasAccess" class="text-center pa-8">
          <v-icon size="64" color="info" class="mb-4">mdi-email-outline</v-icon>
          <h2 class="text-h5 font-weight-bold mb-2">No Activity Found</h2>
          <p class="text-body-1 grey--text">
            No emails or notifications found for this user.
          </p>
        </div>
        <v-data-table
          v-else
        :headers="filteredHeaders"
        :items="filteredItems"
        :loading="loading"
        :items-per-page="10"
        :sort-by="['date']"
        :sort-desc="[true]"
        :single-expand="activeTab !== 'notifications'"
        :expanded.sync="expanded"
        :show-expand="activeTab !== 'notifications'"
        class="elevation-1"
      >
        <template v-slot:item.date="{ item }">
          {{ formatDate(item.date) }}
        </template>

        <template v-slot:item.deliveryState="{ item }">
          <v-chip
            v-if="item.deliveryState"
            :color="getDeliveryStateColor(item.deliveryState)"
            small
            text-color="white"
          >
            {{ item.deliveryState }}
          </v-chip>
          <span v-else>N/A</span>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            small
            text-color="white"
          >
            {{ getStatusText(item) }}
          </v-chip>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <div v-if="item.contentType === 'Email'">
              <v-row>
                <v-col cols="12">
                  <strong>Subject:</strong> {{ item.title }}
                </v-col>
                <v-col cols="12">
                  <strong>Status:</strong> {{ getStatusText(item) }} ({{ item.statusSource }})
                </v-col>
                <v-col cols="12" v-if="item.statusSource === 'SendGrid'">
                  <strong>Message ID:</strong> 
                  <code v-if="item.sendgridMessageIdFull || item.sendgridMessageId" class="ml-2">
                    {{ item.sendgridMessageIdFull || item.sendgridMessageId }}
                  </code>
                  <span v-else class="ml-2 error--text">Missing - tracking issue</span>
                </v-col>
                <v-col cols="12">
                  <strong>From:</strong> {{ item.rawData.from || 'noreply@getreshub.com' }}
                </v-col>
                <v-col cols="12" v-if="item.rawData.delivered || item.rawData.deliveredAt">
                  <strong>Delivered:</strong> 
                  <span v-if="item.rawData.deliveredAt" class="ml-2 text--secondary">
                    {{ formatDate(item.rawData.deliveredAt.toDate()) }}
                  </span>
                  <span v-else class="ml-2 text--secondary">Yes</span>
                </v-col>
                <v-col cols="12" v-if="item.rawData.opened">
                  <strong>Opens:</strong> {{ item.rawData.openCount || 0 }}
                  <span v-if="item.rawData.firstOpenedAt" class="ml-2 text--secondary">
                    (First: {{ formatDate(item.rawData.firstOpenedAt.toDate()) }})
                  </span>
                </v-col>
                <v-col cols="12" v-if="item.rawData.clicked">
                  <strong>Clicks:</strong> {{ item.rawData.clickCount || 0 }}
                  <span v-if="item.rawData.firstClickedAt" class="ml-2 text--secondary">
                    (First: {{ formatDate(item.rawData.firstClickedAt.toDate()) }})
                  </span>
                </v-col>
                <v-col cols="12" v-if="item.rawData.clickedUrls && item.rawData.clickedUrls.length > 0">
                  <strong>Clicked Links:</strong>
                  <v-list dense class="mt-2">
                    <v-list-item v-for="(url, index) in item.rawData.clickedUrls" :key="index">
                      <v-list-item-icon>
                        <v-icon small>mdi-link</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <a :href="url" target="_blank" class="text-decoration-none">
                          {{ url }}
                        </a>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12" v-if="item.rawData.bounced">
                  <v-alert type="error" dense text>
                    <strong>Bounce Reason:</strong> {{ item.rawData.bounceReason || 'Unknown' }}
                  </v-alert>
                </v-col>
                <v-col cols="12" v-if="item.rawData.dropped">
                  <v-alert type="error" dense text>
                    <strong>Drop Reason:</strong> {{ item.rawData.dropReason || 'Unknown' }}
                  </v-alert>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <strong>Title:</strong> {{ item.title }}
            </div>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="text-center pa-4">
            No email activity found
          </div>
        </template>
      </v-data-table>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { API } from '@/api';
import moment from 'moment';
import { cutString } from '@/utils/stringFormat';

export default {
  name: 'StaffNotificationActivity',
  props: {
    userId: {
      type: String,
      required: true
    },
    userEmail: {
      type: String,
      required: true
    },
    facilitiesIds: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      notificationItems: [],
      expanded: [],
      activeTab: 'emails',
      hasAccess: true,
      headers: [
        {
          text: this.$t('UserDetails.NotificationActivity.Headers.Date'),
          value: 'date',
          sortable: true
        },
        {
          text: this.$t('UserDetails.NotificationActivity.Headers.ContentType'),
          value: 'contentType',
          sortable: true
        },
        {
          text: this.$t('UserDetails.NotificationActivity.Headers.Type'),
          value: 'notificationType',
          sortable: true
        },
        {
          text: this.$t('UserDetails.NotificationActivity.Headers.DeliveryState'),
          value: 'deliveryState',
          sortable: true
        },
        {
          text: this.$t('UserDetails.NotificationActivity.Headers.Status'),
          value: 'status',
          sortable: true
        },
        { text: '', value: 'data-table-expand' }
      ]
    };
  },
  computed: {
    filteredItems() {
      if (this.activeTab === 'emails') {
        return this.notificationItems.filter(item => item.contentType === 'Email');
      } else if (this.activeTab === 'notifications') {
        return this.notificationItems.filter(item => item.contentType === 'Notification');
      }
      return this.notificationItems.filter(item => item.contentType === 'Email');
    },
    filteredHeaders() {
      // For notifications-only view: hide delivery state and expand column, add title after date
      if (this.activeTab === 'notifications') {
        const filtered = this.headers.filter(h => h.value !== 'deliveryState' && h.value !== 'data-table-expand');
        const dateHeader = filtered.find(h => h.value === 'date');
        const otherHeaders = filtered.filter(h => h.value !== 'date');
        
        return [
          dateHeader,
          {
            text: this.$t('UserDetails.NotificationActivity.Headers.Title'),
            value: 'title',
            sortable: false
          },
          ...otherHeaders
        ];
      }
      return this.headers;
    },
    emailCount() {
      return this.notificationItems.filter(item => item.contentType === 'Email').length;
    },
    notificationCount() {
      return this.notificationItems.filter(item => item.contentType === 'Notification').length;
    }
  },
  async mounted() {
    await this.loadNotificationActivity();
  },
  methods: {
    cutString,
    
    async loadNotificationActivity() {
      this.loading = true;
      try {
        const currentFacilityId = this.$store.state.authModule?.currentFacilityData?.id;
        
        // Check if the viewed user has access to the current facility
        // Convert to plain array to handle Vue reactive arrays
        const userFacilities = Array.isArray(this.facilitiesIds) ? [...this.facilitiesIds] : [];
        this.hasAccess = userFacilities.includes(currentFacilityId);
        
        if (!this.hasAccess) {
          this.notificationItems = [];
          return;
        }
        
        const result = await API.staff.getNotificationActivity(
          this.userId,
          this.userEmail,
          currentFacilityId
        );
        this.notificationItems = result.items;
      } catch (error) {
        console.error('Error loading notification activity:', error);
        // Don't show error toast for permission-denied, the UI message handles it
        if (error.code !== 'permission-denied') {
          this.$toast.open({
            message: this.$t('GeneralTranslations.UnhandledError'),
            type: 'error'
          });
        }
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      return moment(date).format('MMM D, h:mm A');
    },

    getDeliveryStateColor(state) {
      const colors = {
        SUCCESS: 'success',
        ERROR: 'error',
        PENDING: 'warning',
        UNKNOWN: 'grey'
      };
      return colors[state] || 'grey';
    },

    getStatusColor(status) {
      // Priority order for status colors (highest to lowest priority)
      if (status.includes('Spam Report')) return 'red darken-2'; // Most serious
      if (status.includes('Bounced')) return 'error';
      if (status.includes('Dropped')) return 'error';
      if (status.includes('Unsubscribed')) return 'deep-orange';
      if (status.includes('Group Unsubscribe')) return 'orange darken-1';
      if (status.includes('Group Resubscribe')) return 'teal';
      if (status.includes('Clicked')) return 'primary';
      if (status.includes('Opened')) return 'info';
      if (status.includes('Sent')) return 'orange'; // Covers both "Sent to SendGrid" and "Sent - awaiting"
      
      const colors = {
        Delivered: 'success',
        'Failed to Send': 'error',
        Pending: 'warning',
        Read: 'success',
        Unread: 'grey'
      };
      return colors[status] || 'grey';
    },

    getStatusText(item) {
      // Simplify status text for display in chip
      const status = item.status;
      
      if (status.includes('Clicked')) {
        const openMatch = status.match(/Opened \((\d+)\)/);
        const clickMatch = status.match(/Clicked \((\d+)\)/);
        const opens = openMatch ? openMatch[1] : '0';
        const clicks = clickMatch ? clickMatch[1] : '0';
        return `Opened (${opens}) & Clicked (${clicks})`;
      }
      
      if (status.includes('Opened')) {
        const openMatch = status.match(/Opened \((\d+)\)/);
        const opens = openMatch ? openMatch[1] : '0';
        return `Opened (${opens})`;
      }
      
      return status;
    }
  }
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}
</style>
